import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_LINKS, LOGO_WHITE_URL, HOSTEL_INFO, WHATSAPP_LINK } from '../data/mockData';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(() => window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrolled]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href;
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#1C1C1C]/95 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-[#1C1C1C]/80 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={LOGO_WHITE_URL}
                alt={HOSTEL_INFO.name}
                className="h-12 w-12 rounded-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="hidden sm:block">
                <span className="text-[#F5F0E8] font-bold text-lg tracking-wide font-heading">
                  MISFiTS
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-sm tracking-widest uppercase font-medium transition-colors duration-300 ${
                    isActive(link.href)
                      ? 'text-[#D4A843]'
                      : 'text-[#B0A898] hover:text-[#D4A843]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={HOSTEL_INFO.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#B0A898] hover:text-[#D4A843] transition-colors duration-300"
                title="Follow us on Instagram"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#D4A843] text-[#1C1C1C] px-5 py-2 rounded-full text-xs font-semibold hover:bg-[#E8B940] transition-colors duration-300 uppercase tracking-wider"
              >
                Book Now
                <ArrowRight size={12} />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden text-[#F5F0E8] p-2"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#1C1C1C]/98 backdrop-blur-lg flex flex-col items-center justify-center gap-6"
          >
            {NAV_LINKS.map((link, i) => (
              <motion.div
                key={link.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <Link
                  to={link.href}
                  className={`text-2xl tracking-widest uppercase font-light transition-colors duration-300 ${
                    isActive(link.href) ? 'text-[#D4A843]' : 'text-[#F5F0E8] hover:text-[#D4A843]'
                  }`}
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center gap-2 bg-[#D4A843] text-[#1C1C1C] px-8 py-3 rounded-full text-sm font-semibold"
            >
              Book Now
              <ArrowRight size={14} />
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
